import { BlockchainsMoleculeReatom as BlockchainsMolecule } from "@/app/blockchains/blockchain-molecule.reatom"
import { Blockchain } from "@anaconda/core/blockchain/types"
import { Trans } from "@lingui/react/macro"
import { useMolecule } from "bunshi/react"
import { InsufficientFundsError, isInsufficientFundsError } from "../errors"

export function useToastError(error: unknown, blockchain?: Blockchain) {
  const listingError = getListingError(error, blockchain)
  if (listingError) {
    return listingError
  }

  const unsufficientFundsError = useUnsufficientFundsError(error, blockchain)
  if (unsufficientFundsError) {
    return unsufficientFundsError
  }

  const gasFeeError = getGasFeeError(error)
  if (gasFeeError) {
    return gasFeeError
  }

  const dropClaimExceedLimitError = getDropClaimExceedLimitError(error)
  if (dropClaimExceedLimitError) {
    return dropClaimExceedLimitError
  }

  const notEligibleError = getNotEligibleError(error)
  if (notEligibleError) {
    return notEligibleError
  }

  return getDefaultError(error)
}

export function isError(error: unknown): error is Error {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  )
}

function getGasFeeError(error: unknown) {
  const gasFeeError =
    isError(error) &&
    error.message.includes(
      "maxFeePerGas cannot be less than maxPriorityFeePerGas",
    )

  return gasFeeError
    ? {
        message: (
          <Trans>
            Gas fee is too low.
            <br />
            Adjust gas fee in your wallet and try again.
          </Trans>
        ),
        type: "destructive",
      }
    : undefined
}

function getListingError(error: unknown, blockchain?: Blockchain) {
  const { reatomIsEvmBlockchain } = useMolecule(BlockchainsMolecule)
  const isEvm = blockchain ? reatomIsEvmBlockchain()(blockchain) : false

  if (blockchain === Blockchain.SOLANA && isError(error)) {
    const isListingCancelled =
      error.message.includes("User rejected") ||
      error.message.includes("User denied") ||
      error.message === "Plugin Closed" ||
      error.message === "Approval Denied"

    if (isListingCancelled) {
      return {
        message: <Trans>Transaction was cancelled</Trans>,
        type: "destructive",
      } as const
    }

    if (error.message.trim()) {
      return {
        message: error.message,
        type: "destructive",
      } as const
    }
  }

  if (blockchain === undefined || Blockchain.ECLIPSE === blockchain) {
    const isListingCancelled =
      isError(error) &&
      (error.message === "Plugin Closed" || error.message === "Approval Denied")

    if (isListingCancelled) {
      return {
        message: <Trans>Transaction was cancelled</Trans>,
        type: "destructive",
      } as const
    }
  }

  if (isEvm) {
    const isMintingDisabled =
      isError(error) && error.message.includes("Minting is not enabled")

    if (isMintingDisabled) {
      return {
        message: <Trans>Minting is not enabled yet</Trans>,
        type: "surface",
      } as const
    }

    const isLimitPerWalletExceeded =
      isError(error) && error.message.includes("Mint limit per wallet exceeded")
    if (isLimitPerWalletExceeded) {
      return {
        message: <Trans>Mint limit per wallet was exceeded</Trans>,
        type: "destructive",
      } as const
    }

    const isMaxSupplyWasReached =
      isError(error) && error.message.includes("Max supply reached")

    if (isMaxSupplyWasReached) {
      return {
        message: <Trans>Max supply was reached</Trans>,
        type: "destructive",
      }
    }

    const isListingCancelled =
      isError(error) && error.message.includes("User rejected the request")

    if (isListingCancelled) {
      return {
        message: <Trans>Transaction was cancelled</Trans>,
        type: "destructive",
      } as const
    }
  }

  return undefined
}

function useUnsufficientFundsError(error: unknown, blockchain?: Blockchain) {
  const { reatomAddFundsLink } = useMolecule(BlockchainsMolecule)

  // Handle our custom InsufficientFundsError with structured data
  if (isInsufficientFundsError(error)) {
    const addFundsLink = reatomAddFundsLink()(error.blockchain)

    return {
      message: (
        <div className="flex w-full min-w-0 max-w-xs flex-col gap-1.5">
          <div className="flex flex-col gap-1">
            <div className="break-words font-medium text-sm leading-tight">
              <Trans>Insufficient funds on {error.blockchain}.</Trans>
            </div>
            {addFundsLink && (
              <a
                className="w-fit flex-shrink-0 text-brand text-sm underline"
                rel="noreferrer"
                target="_blank"
                href={addFundsLink}
              >
                <Trans>Add funds</Trans>
              </a>
            )}
          </div>
          <div className="break-words text-foreground/70 text-xs leading-tight">
            <Trans>
              Current balance: {error.formattedCurrentBalance}{" "}
              {error.currency.abbreviation.toUpperCase()}
            </Trans>
          </div>
          <div className="break-words text-foreground/70 text-xs leading-tight">
            <Trans>
              Required: {error.formattedRequiredAmount}{" "}
              {error.currency.abbreviation.toUpperCase()}
            </Trans>
            {error.quantity && error.quantity > 1 && (
              <div className="mt-0.5 text-foreground/60 text-xs">
                <Trans>(for {error.quantity} items)</Trans>
              </div>
            )}
          </div>
          {error.formattedBasePrice &&
            error.formattedFees &&
            Number(error.fees || 0) > 0 && (
              <div className="space-y-0.5 border-foreground/20 border-l pl-2 text-foreground/60 text-xs">
                <div className="break-words leading-tight">
                  •{" "}
                  <Trans>
                    Base price: {error.formattedBasePrice}{" "}
                    {error.currency.abbreviation.toUpperCase()}
                  </Trans>
                </div>
                <div className="break-words leading-tight">
                  •{" "}
                  <Trans>
                    Fees: {error.formattedFees}{" "}
                    {error.currency.abbreviation.toUpperCase()}
                  </Trans>
                </div>
              </div>
            )}
        </div>
      ),
      type: "surface",
    } as const
  }

  // Fallback to existing logic for other insufficient funds errors
  const addFundsLink = blockchain ? reatomAddFundsLink()(blockchain) : undefined

  const isAnotherInsufficientFunds =
    isError(error) &&
    (error.message.includes("insufficient funds") ||
      error.message.includes("Transfer: insufficient lamports") ||
      error.message.includes("not have enough funds") ||
      // Seaport buyer short of the listing's payment token (e.g. WETH)
      error.message.includes("does not have the balances needed") ||
      // Rarible exchange pulling more of a payment token than the buyer has
      error.message.includes("transfer amount exceeds balance") ||
      error.message.includes("gas value exceeds maximum limit"))

  const isInsufficientFunds =
    typeof error === "object" &&
    error !== null &&
    "transactionMessage" in error &&
    error.transactionMessage ===
      "Transaction simulation failed: Attempt to debit an account but found no record of a prior credit."

  if (isInsufficientFunds || isAnotherInsufficientFunds) {
    return {
      message: (
        <div className="flex gap-x-1">
          <Trans>Not enough funds.</Trans>
          {addFundsLink && (
            <a
              className="text-brand"
              rel="noreferrer"
              target="_blank"
              href={addFundsLink}
            >
              <Trans>Add funds</Trans>
            </a>
          )}
        </div>
      ),
      type: "surface",
    } as const
  }

  return undefined
}

function getDropClaimExceedLimitError(error: unknown) {
  const dropClaimExceedLimit =
    isError(error) && error.message.includes("DropClaimExceedLimit")

  return dropClaimExceedLimit
    ? {
        message: <Trans>Oops! Wallet limit exceeded for this item.</Trans>,
        type: "destructive",
      }
    : undefined
}

function getNotEligibleError(error: unknown) {
  const dropClaimExceedLimit =
    isError(error) && error.message.includes("DropClaimExceedLimit - 0")

  return dropClaimExceedLimit
    ? {
        message: (
          <Trans>Oops! You're not eligible to mint in this phase.</Trans>
        ),
        type: "destructive",
      }
    : undefined
}

function getDefaultError(error: unknown) {
  return {
    message: <Trans>Oops! Something went wrong</Trans>,
    type: "destructive",
  } as const
}
